import * as React from 'react';

import { LucideX } from 'lucide-react';

import { touchModeKeyboardShortcuts } from '../../lib/keyboard';
import { cn } from '../../lib/stylingUtils';
import { ColorScheme } from '../../types/theme';
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  VirtualKeyboardProvider,
  VirtualQwertyKeyboard,
  useVirtualKeyboardProvider,
} from '../ui';
import { Scrollable } from '../ui/content/Scrollable';

export type ConsoleTouchDialogProps = {
  children: React.ReactNode;
  touchModeEnabled: boolean;
  effectiveColorScheme: ColorScheme;
  isOpen: boolean;
  showKeyboard?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  dialogClassName?: string;
  classNameHeader?: string;
  contentStyle?: React.CSSProperties;
  onOpenChange: (isOpen: boolean) => void;
};

export const ConsoleTouchDialog = ({
  touchModeEnabled,
  isOpen,
  header,
  children,
  footer,
  onOpenChange,
  effectiveColorScheme,
  className,
  dialogClassName,
  classNameHeader,
  contentStyle,
}: ConsoleTouchDialogProps) => {
  const { keyboardRef, keyboardChangeListener, ...virtualKeyboardContext } =
    useVirtualKeyboardProvider(touchModeEnabled);

  const { shouldRenderKeyboard, hideKeyboard } = virtualKeyboardContext;

  const hasCustomPosition =
    contentStyle &&
    (contentStyle.left !== undefined || contentStyle.width !== undefined);

  return (
    <VirtualKeyboardProvider value={virtualKeyboardContext}>
      <DialogOverlay
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        className={cn('@ConsoleTouchDialog', className)}
      >
        <DialogContent
          closeButton={false}
          dialogClassName={cn(
            'flex flex-col overflow-hidden rounded-lg bg-neutral/10 pb-5 shadow-xl',
            !hasCustomPosition && 'h-[50vh] w-[80vw] max-w-[1200px]',
            shouldRenderKeyboard && 'pb-0',
            dialogClassName,
          )}
          className="rounded-lg border-none bg-white"
          style={contentStyle}
        >
          {header && (
            <DialogHeader
              className={cn(
                '@ConsoleTouchDialogHeader',
                '!m-0 flex flex-row items-center justify-between gap-5',
                classNameHeader,
              )}
            >
              {header}
              <Button
                variant="outline"
                color="neutral"
                className="min-h-12"
                onPress={() => onOpenChange(false)}
                keyboardShortcut={touchModeKeyboardShortcuts.escape}
              >
                <LucideX size={24} />
              </Button>
            </DialogHeader>
          )}
          <DialogMain className="!overflow-hidden">
            <div
              className={cn(
                'flex h-full flex-col gap-6 rounded-lg bg-white p-4',
              )}
            >
              <Scrollable
                themeMode={effectiveColorScheme}
                className="flex-1"
                style={{ height: 0 }}
              >
                {children}
              </Scrollable>
            </div>
          </DialogMain>

          {footer && <DialogFooter className="!m-0">{footer}</DialogFooter>}

          {shouldRenderKeyboard && (
            <VirtualQwertyKeyboard
              ref={keyboardRef}
              onChange={(value: string) =>
                keyboardChangeListener.current?.(value)
              }
              onHide={() => hideKeyboard()}
            />
          )}
        </DialogContent>
      </DialogOverlay>
    </VirtualKeyboardProvider>
  );
};
